import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';
import OrganizationAccessRights from './OrganizationAccessRights';
import PlatformRoles from './PlatformRoles';

const Organizations = () => {
  // Helper function to get appropriate badge color for subscription status

  // Toggle state for settings sections in modal
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [showSecuritySettings, setShowSecuritySettings] = useState(false);
  const getSubscriptionStatusBadgeColor = (status) => {
    if (!status) return 'secondary';

    const normalizedStatus = status.toLowerCase().trim();

    switch (normalizedStatus) {
      case 'active':
        return 'success';
      case 'renewed':
        return 'success';
      case 'incomplete':
        return 'warning';
      case 'grace_period':
        return 'info';
      case 'past_due':
        return 'danger';
      case 'paused':
        return 'secondary';
      case 'canceled':
        return 'dark';
      case 'expired':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [showAccessRights, setShowAccessRights] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [showRoles, setShowRoles] = useState(false);
  const [codeValidation, setCodeValidation] = useState({ isValid: true, message: '' });
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [validationTimeout, setValidationTimeout] = useState(null);
  const [erpIdValidation, setErpIdValidation] = useState({ isValid: true, message: '' });
  const [isValidatingErpId, setIsValidatingErpId] = useState(false);
  const [erpIdValidationTimeout, setErpIdValidationTimeout] = useState(null);

  // Default settings state
  const [defaultSettings, setDefaultSettings] = useState({
    userSettings: null,
    notificationSettings: null,
    securitySettings: null,
    loading: false
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    erp_id: '',
    userSettings: '{}',
    notificationSettings: '{}',
    security_settings: '{}'
  });

  useEffect(() => {
    loadOrganizations();
  }, [currentPage, search, itemsPerPage]);

  // Cleanup validation timeouts on unmount
  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
      if (erpIdValidationTimeout) {
        clearTimeout(erpIdValidationTimeout);
      }
    };
  }, [validationTimeout, erpIdValidationTimeout]);

  const loadOrganizations = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiService.getOrganizations(currentPage, itemsPerPage, search);
      if (response.success) {
        setOrganizations(response.data || []);
        setTotalPages(Math.ceil((response.pagination?.total || 0) / itemsPerPage));
      } else {
        setError(response.error || 'Failed to load organizations');
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };



  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      setCurrentPage(1);
      setSearch(e.target.value);
    }
  };

  const handleShowModal = async (org = null) => {
    if (org) {
      setShowUserSettings(false);
      setShowNotificationSettings(false);
      setShowSecuritySettings(false);
      setEditingOrg(org);
      setFormData({
        name: org.name || '',
        code: org.code || '',
        erp_id: org.erp_id || '',
        userSettings: JSON.stringify(org.user_settings || {}, null, 2),
        notificationSettings: JSON.stringify(org.notification_settings || {}, null, 2),
        security_settings: JSON.stringify(org.security_settings || {}, null, 2)
      });
      setShowModal(true);
    } else {
      setShowUserSettings(false);
      setShowNotificationSettings(false);
      setShowSecuritySettings(false);
      setEditingOrg(null);
      setShowModal(true);

      // Load default settings for new organization
      setDefaultSettings(prev => ({ ...prev, loading: true }));

      try {
        // Load all three default settings in parallel
        const [userSettingsResponse, notificationSettingsResponse, securitySettingsResponse] = await Promise.all([
          apiService.getOrganizationDefaultSettingByCode('user_settings'),
          apiService.getOrganizationDefaultSettingByCode('notification_settings'),
          apiService.getOrganizationDefaultSettingByCode('security_settings'),
          apiService.getOrganizationDefaultSettingByCode('product_data'),
          apiService.getOrganizationDefaultSettingByCode('platform_access_rights')
        ]);

        const loadedUserSettings = userSettingsResponse.success ? userSettingsResponse.data?.value : {
          theme: "light",
          timezone: "EAT",
          language: "en",
          default_sales_rep_screen: "dashboard",
          allow_theme_change: false,
          allow_language_change: false
        };

        const loadedNotificationSettings = notificationSettingsResponse.success ? notificationSettingsResponse.data?.value : {
          user: {
            upcoming_platform_maintenance: false,
            user_account_changes: false,
            transaction_failures: false,
            transcation_success: false
          },
          admin: {
            account_auto_lock: true,
            account_expiry: true,
            notification_channels: {
              email: true,
              sms: false,
              in_platform: true,
              whatsapp: false
            }
          }
        };

        const loadedSecuritySettings = securitySettingsResponse.success ? securitySettingsResponse.data?.value : {
          password: {
            min_length: 8,
            require_special_chars: true,
            require_numbers: true,
            require_uppercase: true,
            require_lowercase: true,
            expiry_days: 90,
            max_login_attempts: 5,
            account_inactivity_days: 180,
            enable_two_factor: false,
            session_timeout: 60
          }
        };

        setDefaultSettings({
          userSettings: loadedUserSettings,
          notificationSettings: loadedNotificationSettings,
          securitySettings: loadedSecuritySettings,
          loading: false
        });

        setFormData({
          name: '',
          code: '',
          erp_id: '',
          userSettings: JSON.stringify(loadedUserSettings, null, 2),
          notificationSettings: JSON.stringify(loadedNotificationSettings, null, 2),
          security_settings: JSON.stringify(loadedSecuritySettings, null, 2)
        });
      } catch (error) {
        console.error('Error loading default settings:', error);
        setDefaultSettings(prev => ({ ...prev, loading: false }));
      }
    }
  };

  const handleCloseModal = () => {
    // Clear validation timeouts
    if (validationTimeout) {
      clearTimeout(validationTimeout);
      setValidationTimeout(null);
    }
    if (erpIdValidationTimeout) {
      clearTimeout(erpIdValidationTimeout);
      setErpIdValidationTimeout(null);
    }

    setShowModal(false);
    setEditingOrg(null);
    setFormData({
      name: '',
      code: '',
      erp_id: '',
      userSettings: '{}',
      notificationSettings: '{}',
      security_settings: '{}'
    });
    setCodeValidation({ isValid: true, message: '' });
    setIsValidatingCode(false);
    setErpIdValidation({ isValid: true, message: '' });
    setIsValidatingErpId(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Convert code to uppercase
    const processedValue = name === 'code' ? value.toUpperCase() : value;

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Validate code field in real-time with debouncing
    if (name === 'code') {
      // Clear existing timeout
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }

      // Set new timeout for validation
      const newTimeout = setTimeout(() => {
        validateCode(processedValue.trim());
      }, 500); // 500ms debounce

      setValidationTimeout(newTimeout);
    }

    // Validate ERP ID field in real-time with debouncing
    if (name === 'erp_id') {
      if (erpIdValidationTimeout) {
        clearTimeout(erpIdValidationTimeout);
      }

      const newTimeout = setTimeout(() => {
        validateErpId(processedValue.trim());
      }, 500);

      setErpIdValidationTimeout(newTimeout);
    }
  };

  const validateErpId = async (erpId) => {
    // Reset validation state
    setErpIdValidation({ isValid: true, message: '' });

    // Empty ERP ID is not allowed (it's mandatory)
    if (!erpId || erpId.length === 0) {
      setErpIdValidation({ isValid: false, message: 'ERP ID is required' });
      return;
    }

    // If editing and ERP ID hasn't changed, skip uniqueness check
    if (editingOrg && editingOrg.erp_id && editingOrg.erp_id.trim() === erpId) {
      setErpIdValidation({ isValid: true, message: 'Current ERP ID' });
      return;
    }

    // Check uniqueness against existing organizations
    setIsValidatingErpId(true);

    try {
      const orgsResponse = await apiService.getOrganizations(1, 1000, '');

      if (orgsResponse.success && orgsResponse.data) {
        const existingOrg = orgsResponse.data.find(org =>
          org.erp_id &&
          org.erp_id.trim().toLowerCase() === erpId.toLowerCase() &&
          (!editingOrg || org.id !== editingOrg.id)
        );

        if (existingOrg) {
          setErpIdValidation({ isValid: false, message: `This ERP ID is already assigned to "${existingOrg.name}". Please use a different ERP ID.` });
        } else {
          setErpIdValidation({ isValid: true, message: 'ERP ID is available' });
        }
      } else {
        setErpIdValidation({ isValid: true, message: 'Unable to verify uniqueness' });
      }
    } catch (error) {
      console.error('Error validating ERP ID:', error);
      setErpIdValidation({ isValid: true, message: 'Unable to verify uniqueness' });
    } finally {
      setIsValidatingErpId(false);
    }
  };

  const validateJSON = (jsonString) => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  const validateCode = async (code) => {
    // Reset validation state
    setCodeValidation({ isValid: true, message: '' });

    // Check if code is exactly 3 characters
    if (code.length === 0) {
      setCodeValidation({ isValid: true, message: '' });
      return;
    }

    if (code.length !== 3) {
      setCodeValidation({ isValid: false, message: 'Code must be exactly 3 characters long' });
      return;
    }

    // Check if code contains only alphanumeric characters
    const alphanumericRegex = /^[A-Za-z0-9]+$/;
    if (!alphanumericRegex.test(code)) {
      setCodeValidation({ isValid: false, message: 'Code must contain only letters and numbers' });
      return;
    }

    // Check uniqueness for new organizations or when code is changed for existing organization
    if (!editingOrg || (editingOrg && editingOrg.code.toUpperCase() !== code.toUpperCase())) {
      setIsValidatingCode(true);

      try {
        // First try the dedicated code check endpoint
        const codeCheckResponse = await apiService.checkOrganizationCodeExists(code);

        if (codeCheckResponse.success) {
          if (codeCheckResponse.exists) {
            setCodeValidation({ isValid: false, message: 'This code is already in use. Please choose a different code.' });
          } else {
            setCodeValidation({ isValid: true, message: 'Code is available' });
          }
        } else {
          // Fallback: Check by fetching all organizations and comparing codes
          const orgsResponse = await apiService.getOrganizations(1, 1000, '');

          if (orgsResponse.success && orgsResponse.data) {
            const existingOrg = orgsResponse.data.find(org =>
              org.code &&
              org.code.toLowerCase() === code.toLowerCase() &&
              (!editingOrg || org.id !== editingOrg.id) // Exclude current organization when editing
            );

            if (existingOrg) {
              setCodeValidation({ isValid: false, message: 'This code is already in use. Please choose a different code.' });
            } else {
              setCodeValidation({ isValid: true, message: 'Code is available' });
            }
          } else {
            // If we can't check, assume it's available but show a warning
            setCodeValidation({ isValid: true, message: 'Unable to verify uniqueness' });
          }
        }
      } catch (error) {
        console.error('Error validating code:', error);
        setCodeValidation({ isValid: true, message: 'Unable to verify uniqueness' });
      } finally {
        setIsValidatingCode(false);
      }
    } else if (editingOrg && editingOrg.code.toUpperCase() === code.toUpperCase()) {
      // Code hasn't changed for existing organization
      setCodeValidation({ isValid: true, message: 'Current organization code' });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    setError('');

    // Validate name field (required for both create and edit)
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      setError('Organization name is required and cannot be empty');
      return;
    }

    // Validate ERP ID uniqueness (for both create and edit)
    if (!erpIdValidation.isValid) {
      setError('Please fix the ERP ID validation errors before saving');
      return;
    }

    if (!editingOrg) {
      // For creating new organization, validate all fields

      // Validate code field
      const trimmedCode = formData.code.trim();
      if (trimmedCode.length !== 3) {
        setError('Code must be exactly 3 characters long');
        return;
      }

      if (!codeValidation.isValid) {
        setError('Please fix the code validation errors before saving');
        return;
      }

      // Validate JSON fields
      if (!validateJSON(formData.userSettings)) {
        setError('Invalid JSON in User Settings');
        return;
      }
      if (!validateJSON(formData.notificationSettings)) {
        setError('Invalid JSON in Notification Settings');
        return;
      }
      if (!validateJSON(formData.security_settings)) {
        setError('Invalid JSON in Security Settings');
        return;
      }
    }

    setSaving(true);

    try {
      let orgData;

      if (editingOrg) {
        // For editing, allow name and ERP ID to be updated
        orgData = {
          name: trimmedName,
          erp_id: formData.erp_id.trim()
        };
      } else {
        // For creating new organization, include all fields
        orgData = {
          name: trimmedName,
          code: formData.code.trim(),
          erp_id: formData.erp_id.trim(),
          user_settings: JSON.parse(formData.userSettings),
          notification_settings: JSON.parse(formData.notificationSettings),
          security_settings: JSON.parse(formData.security_settings)
        };
      }

      let response;
      if (editingOrg) {
        response = await apiService.updateOrganization(editingOrg.id, orgData);
      } else {
        response = await apiService.createOrganization(orgData);
      }

      if (response.success) {
        handleCloseModal();
        await loadOrganizations();
      } else {
        setError(response.error || 'Failed to save organization');
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      setError('Failed to save organization');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete the organization "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deleteOrganization(id);
      if (response.success) {
        await loadOrganizations();
      } else {
        setError(response.error || 'Failed to delete organization');
      }
    } catch (error) {
      console.error('Error deleting organization:', error);
      setError('Failed to delete organization');
    }
  };

  const handleViewRoles = (organizationId) => {
    const organization = organizations.find(org => org.id === organizationId);
    if (organization) {
      setSelectedOrganization(organization);
      setShowRoles(true);
    }
  };

  const handleViewAccessRights = (organizationId) => {
    const organization = organizations.find(org => org.id === organizationId);
    if (organization) {
      setSelectedOrganization(organization);
      setShowAccessRights(true);
    }
  };

  const handleCloseAccessRights = () => {
    setShowAccessRights(false);
    setSelectedOrganization(null);
  };

  const handleCloseRoles = () => {
    setShowRoles(false);
    setSelectedOrganization(null);
  };

  const handleGenerateAccessAndInvoice = async (subscription_id, organizationName) => {
    if (!window.confirm(`Generate Access and Invoice for "${organizationName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiService.generateAccessAndInvoice(subscription_id);
      if (response.success) {
        // Reload organizations to get updated status
        await loadOrganizations();
        // You could also show a success message here
        alert(`Main admin account created and initial invoice generated successfully!. Use the Platform users section to reset the main admin account password.`);
      } else {
        setError(response.error || 'Failed to generate Access and Invoice. Contact Support.');
      }
    } catch (error) {
      console.error('Error generating Access and Invoice:', error);
      setError('Failed to generate Access and Invoice. Contact Support.');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  return (
    <div>
      {showAccessRights && selectedOrganization ? (
        <OrganizationAccessRights
          organizationId={selectedOrganization.id}
          organizationName={selectedOrganization.name}
          onClose={handleCloseAccessRights}
        />
      ) : showRoles && selectedOrganization ? (
        <PlatformRoles
          organizationId={selectedOrganization.id}
          organizationName={selectedOrganization.name}
          onClose={handleCloseRoles}
        />
      ) : (
        <>
          <Row>
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                  <i className="bi bi-buildings me-2"></i>
                  Organizations
                </h2>
                <Button variant="primary" onClick={() => handleShowModal()}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Organization
                </Button>
              </div>
            </Col>
          </Row>

          {error && (
            <Row className="mb-4">
              <Col>
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </Alert>
              </Col>
            </Row>
          )}

          {/* Search and Filter */}
          <Row className="mb-4">
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Search organizations..."
                onKeyDown={handleSearch}
                onBlur={handleSearch}
              />
            </Col>
            <Col md={6} className="text-end">
              <Form.Select
                style={{ width: 'auto', display: 'inline-block' }}
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
              </Form.Select>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card>
                <Card.Body className="p-0">
                  {loading ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </div>
                  ) : organizations.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-buildings display-1"></i>
                      <div className="mt-2">No organizations found</div>
                    </div>
                  ) : (
                    <Table responsive hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Organization</th>
                          <th>Code</th>
                          <th>ERP ID</th>
                          <th>Total Users</th>
                          <th>Active Users</th>
                          <th>Inactive Users</th>
                          <th>Locked Users</th>
                          <th>Subscription Status</th>
                          <th>Created</th>
                          <th>Updated</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {organizations.map((org) => (
                          <tr key={org.id}>
                            <td>
                              <div className="fw-medium">{org.name}</div>
                            </td>
                            <td>
                              <code>{org.code}</code>
                            </td>
                            <td>
                              <span className="text-muted">{org.erp_id || '-'}</span>
                            </td>
                            <td>
                              <Badge bg="info">{org.total_user_count || 0}</Badge>
                            </td>
                            <td>
                              <Badge bg="info">{org.active_user_count || 0}</Badge>
                            </td>
                            <td>
                              <Badge bg="info">{org.inactive_user_count || 0}</Badge>
                            </td>
                            <td>
                              <Badge bg="info">{org.locked_user_count || 0}</Badge>
                            </td>
                            <td>
                              <Badge bg={getSubscriptionStatusBadgeColor(org.subscription_status)}>
                                {org.subscription_status || 'no subscription'}
                              </Badge>
                            </td>
                            <td>{apiService.formatDate(org.created_at)}</td>
                            <td>{apiService.formatDate(org.last_updated_at)}</td>
                            <td>
                              <div className="d-flex gap-1">
                                {(org.subscription_status || '').toLowerCase() === 'active' && (
                                  <>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleViewRoles(org.id)}
                                      title="View Roles"
                                    >
                                      <i className="bi bi-person-badge"></i>
                                    </Button>
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => handleViewAccessRights(org.id)}
                                      title="View Access Rights"
                                    >
                                      <i className="bi bi-shield-check"></i>
                                    </Button>
                                  </>
                                )}
                                {(org.subscription_status || '').toLowerCase() === 'incomplete' && (
                                  <Button
                                    variant="outline-warning"
                                    size="sm"
                                    onClick={() => handleGenerateAccessAndInvoice(org.subscription_id, org.name)}
                                    title="Generate Access & Invoice"
                                  >
                                    <i className="bi bi-receipt me-1"></i>
                                    Generate Access & Invoice
                                  </Button>
                                )}
                                {(org.subscription_status || '').toLowerCase() === '' && (
                                  <div><i>Create a subscription (under subscription module)</i></div>
                                )}
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => handleShowModal(org)}
                                  title="Edit"
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Card.Footer>
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="text-muted">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, organizations.length)} of {organizations.length} entries
                      </div>
                      <Pagination className="mb-0">
                        <Pagination.Prev
                          disabled={currentPage === 1}
                          onClick={() => handlePageChange(currentPage - 1)}
                        />
                        {[...Array(totalPages)].map((_, i) => (
                          <Pagination.Item
                            key={i + 1}
                            active={i + 1 === currentPage}
                            onClick={() => handlePageChange(i + 1)}
                          >
                            {i + 1}
                          </Pagination.Item>
                        ))}
                        <Pagination.Next
                          disabled={currentPage === totalPages}
                          onClick={() => handlePageChange(currentPage + 1)}
                        />
                      </Pagination>
                    </div>
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          {/* Create/Edit Modal */}
          <Modal show={showModal} onHide={handleCloseModal} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>
                {editingOrg ? 'Edit Organization' : 'Add Organization'}
              </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSave}>
              <Modal.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Organization Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Code *</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          name="code"
                          value={formData.code}
                          onChange={handleInputChange}
                          required
                          maxLength={3}
                          placeholder="ABC"
                          style={{ textTransform: 'uppercase' }}
                          isInvalid={!codeValidation.isValid && formData.code.length > 0}
                          isValid={codeValidation.isValid && formData.code.length === 3 && codeValidation.message === 'Code is available'}
                          readOnly={!!editingOrg}
                        />
                        {isValidatingCode && !editingOrg && (
                          <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <Spinner size="sm" animation="border" />
                          </div>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {codeValidation.message}
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="valid">
                          {codeValidation.message}
                        </Form.Control.Feedback>
                      </div>
                      <Form.Text className="text-muted">
                        {editingOrg ? 'Organization code cannot be modified' : 'Must be exactly 3 alphanumeric characters and unique'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>ERP ID *</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          name="erp_id"
                          value={formData.erp_id}
                          onChange={handleInputChange}
                          placeholder="Enter ERP ID"
                          required
                          isInvalid={!erpIdValidation.isValid}
                          isValid={erpIdValidation.isValid && formData.erp_id.trim().length > 0 && erpIdValidation.message === 'ERP ID is available'}
                        />
                        {isValidatingErpId && (
                          <div className="position-absolute" style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                            <Spinner size="sm" animation="border" />
                          </div>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {erpIdValidation.message}
                        </Form.Control.Feedback>
                        <Form.Control.Feedback type="valid">
                          {erpIdValidation.message}
                        </Form.Control.Feedback>
                      </div>
                      <Form.Text className="text-muted">
                        Required. Must be unique across all organizations.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Settings Display */}
                {!editingOrg ? (
                  <>
                    {/* Adding New Organization - Show Default Settings (Read-only) */}
                    <div className="mb-4">
                      <h6 className="text-primary mb-3">
                        <i className="bi bi-info-circle me-2"></i>
                        Default Settings Preview
                      </h6>
                      <p className="text-muted small mb-3">
                        The following settings will be applied to this organization based on system defaults.
                        The organization administrator can modify these later through the organization settings.
                      </p>
                    </div>

                    {/* User Settings Toggle */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-person-gear me-2"></i>
                        User Settings (Default)
                        <Button
                          variant={showUserSettings ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          className="ms-2"
                          onClick={() => setShowUserSettings((v) => !v)}
                          aria-expanded={showUserSettings}
                        >
                          {showUserSettings ? "Hide" : "Show"}
                        </Button>
                      </Form.Label>
                      {showUserSettings && (
                        <div className="border rounded p-3 bg-light">
                          {defaultSettings.loading ? (
                            <div className="text-center">
                              <Spinner size="sm" /> Loading default settings...
                            </div>
                          ) : (
                            <pre className="mb-0 small text-dark">
                              {formData.userSettings}
                            </pre>
                          )}
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Default user interface preferences, themes, language settings, etc.
                      </Form.Text>
                    </Form.Group>

                    {/* Notification Settings Toggle */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-bell me-2"></i>
                        Notification Settings (Default)
                        <Button
                          variant={showNotificationSettings ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          className="ms-2"
                          onClick={() => setShowNotificationSettings((v) => !v)}
                          aria-expanded={showNotificationSettings}
                        >
                          {showNotificationSettings ? "Hide" : "Show"}
                        </Button>
                      </Form.Label>
                      {showNotificationSettings && (
                        <div className="border rounded p-3 bg-light">
                          {defaultSettings.loading ? (
                            <div className="text-center">
                              <Spinner size="sm" /> Loading default settings...
                            </div>
                          ) : (
                            <pre className="mb-0 small text-dark">
                              {formData.notificationSettings}
                            </pre>
                          )}
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Default notification preferences, channels, and delivery settings.
                      </Form.Text>
                    </Form.Group>

                    {/* Security Settings Toggle */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-shield-lock me-2"></i>
                        Security Settings (Default)
                        <Button
                          variant={showSecuritySettings ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          className="ms-2"
                          onClick={() => setShowSecuritySettings((v) => !v)}
                          aria-expanded={showSecuritySettings}
                        >
                          {showSecuritySettings ? "Hide" : "Show"}
                        </Button>
                      </Form.Label>
                      {showSecuritySettings && (
                        <div className="border rounded p-3 bg-light">
                          {defaultSettings.loading ? (
                            <div className="text-center">
                              <Spinner size="sm" /> Loading default settings...
                            </div>
                          ) : (
                            <pre className="mb-0 small text-dark">
                              {formData.security_settings}
                            </pre>
                          )}
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Default security policies, password requirements, and access controls.
                      </Form.Text>
                    </Form.Group>
                  </>
                ) : (
                  <>
                    {/* Editing Existing Organization - Show Read-only Settings */}
                    <div className="mb-4">
                      <h6 className="text-primary mb-3">
                        <i className="bi bi-info-circle me-2"></i>
                        Current Organization Settings
                      </h6>
                      <p className="text-muted small mb-3">
                        The following settings are currently configured for this organization.
                        These settings cannot be modified from this interface.
                      </p>
                    </div>

                    {/* User Settings Toggle (Edit) */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-person-gear me-2"></i>
                        User Settings (Read-only)
                        <Button
                          variant={showUserSettings ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          className="ms-2"
                          onClick={() => setShowUserSettings((v) => !v)}
                          aria-expanded={showUserSettings}
                        >
                          {showUserSettings ? "Hide" : "Show"}
                        </Button>
                      </Form.Label>
                      {showUserSettings && (
                        <div className="border rounded p-3 bg-light">
                          <pre className="mb-0 small text-dark">
                            {formData.userSettings}
                          </pre>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        User interface preferences, default views, notification settings, etc.
                      </Form.Text>
                    </Form.Group>

                    {/* Notification Settings Toggle (Edit) */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-bell me-2"></i>
                        Notification Settings (Read-only)
                        <Button
                          variant={showNotificationSettings ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          className="ms-2"
                          onClick={() => setShowNotificationSettings((v) => !v)}
                          aria-expanded={showNotificationSettings}
                        >
                          {showNotificationSettings ? "Hide" : "Show"}
                        </Button>
                      </Form.Label>
                      {showNotificationSettings && (
                        <div className="border rounded p-3 bg-light">
                          <pre className="mb-0 small text-dark">
                            {formData.notificationSettings}
                          </pre>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Notification preferences, delivery methods, frequency settings, etc.
                      </Form.Text>
                    </Form.Group>

                    {/* Security Settings Toggle (Edit) */}
                    <Form.Group className="mb-3">
                      <Form.Label>
                        <i className="bi bi-shield-lock me-2"></i>
                        Security Settings (Read-only)
                        <Button
                          variant={showSecuritySettings ? "outline-primary" : "outline-secondary"}
                          size="sm"
                          className="ms-2"
                          onClick={() => setShowSecuritySettings((v) => !v)}
                          aria-expanded={showSecuritySettings}
                        >
                          {showSecuritySettings ? "Hide" : "Show"}
                        </Button>
                      </Form.Label>
                      {showSecuritySettings && (
                        <div className="border rounded p-3 bg-light">
                          <pre className="mb-0 small text-dark">
                            {formData.security_settings}
                          </pre>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        Security policies, access controls, audit settings, etc.
                      </Form.Text>
                    </Form.Group>
                  </>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving || isValidatingErpId || !erpIdValidation.isValid || (!editingOrg && (isValidatingCode || !codeValidation.isValid))}
                >
                  {saving ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                      Saving...
                    </>
                  ) : (!editingOrg && isValidatingCode) ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                      Validating...
                    </>
                  ) : (
                    editingOrg ? 'Update Organization' : 'Save Organization'
                  )}
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </>
      )}
    </div>
  );
};

export default Organizations;
